import { UzFormField, UzInput } from '@utazzo/ui';
import { useFieldArray, type UseFormReturn } from 'react-hook-form';

import { AddRowButton, RowShell } from './RowShell';

import type { CreateWebsiteDestinationPageRequest } from '@utazzo/shared';

import {
  ReelCheckBar,
  reelCheckBorderClass,
  reelCheckError,
  reelCheckProvider,
  useReelCheck,
} from '@/components/reel-check/ReelCheck';
import { MediaDropField } from '@/features/website-cms/components/MediaDropField';

export const ReelsEditor = ({
  form,
  disabled,
}: {
  form: UseFormReturn<CreateWebsiteDestinationPageRequest>;
  disabled?: boolean;
}) => {
  const { fields, append, remove, move } = useFieldArray({ control: form.control, name: 'reels' });
  return (
    <div className="space-y-3">
      {fields.map((f, i) => (
        <ReelRow
          key={f.id}
          form={form}
          index={i}
          total={fields.length}
          disabled={disabled}
          onMove={move}
          onRemove={remove}
        />
      ))}
      <AddRowButton
        label="Add reel"
        disabled={disabled}
        onClick={() => append({ url: '', posterMediaId: null })}
      />
    </div>
  );
};

const ReelRow = ({
  form,
  index,
  total,
  disabled,
  onMove,
  onRemove,
}: {
  form: UseFormReturn<CreateWebsiteDestinationPageRequest>;
  index: number;
  total: number;
  disabled?: boolean;
  onMove: (from: number, to: number) => void;
  onRemove: (index: number) => void;
}): React.JSX.Element => {
  const url = form.watch(`reels.${index}.url`) ?? '';
  const { state, check } = useReelCheck(url);
  const liveError = reelCheckError(state);
  const isInstagram = reelCheckProvider(state) === 'instagram';

  return (
    <RowShell index={index} total={total} disabled={disabled} onMove={onMove} onRemove={onRemove}>
      <UzFormField name={`reels.${index}.url` as const}>
        {({ field, error }) => (
          <div className="space-y-2">
            <UzInput
              {...field}
              value={(field.value as string | null) ?? ''}
              label="Reel URL"
              placeholder="YouTube Shorts or Instagram Reel link"
              error={error ?? liveError}
              className={liveError ? undefined : reelCheckBorderClass(state)}
              disabled={disabled}
            />
            <ReelCheckBar state={state} url={url} onFetch={check} disabled={disabled} />
          </div>
        )}
      </UzFormField>
      <UzFormField name={`reels.${index}.posterMediaId` as const}>
        {({ field }) => (
          <div className="space-y-1 lg:col-span-2">
            <label className="text-sm font-medium text-zinc-700">
              Poster image {isInstagram ? '(recommended for Instagram)' : '(optional)'}
            </label>
            <p className="text-xs text-zinc-500">
              {isInstagram
                ? 'Instagram does not provide auto thumbnails. Upload a vertical cover image for the card preview.'
                : 'Optional custom cover. YouTube Shorts use an auto thumbnail when this is empty.'}
            </p>
            <MediaDropField
              value={(field.value as string | null) ?? null}
              kind="image"
              folder="destination-pages"
              disabled={disabled}
              onChange={(v) => field.onChange(v)}
            />
          </div>
        )}
      </UzFormField>
    </RowShell>
  );
};
